import { phAddToCart } from '@theme/ph-cart-add';

/* PHASES product page: variant selection, gallery sync, add to cart / buy it now, mobile sticky bar */

function initPdp(root) {
  if (root.dataset.phReady) return;
  root.dataset.phReady = 'true';

  /** @type {{id:number, options:string[], available:boolean, price:string, compare:string|null, media:number|null}[]} */
  const variants = JSON.parse(root.querySelector('[data-ph-variants]')?.textContent || '[]');
  const form = /** @type {HTMLFormElement|null} */ (root.querySelector('[data-ph-form]'));
  const idInput = /** @type {HTMLInputElement|null} */ (root.querySelector('[data-ph-variant-input]'));
  const fieldsets = [...root.querySelectorAll('[data-ph-option]')];
  const stickySelects = [...root.querySelectorAll('[data-ph-sticky-option]')];
  const track = root.querySelector('[data-ph-track]');
  const slides = [...root.querySelectorAll('.ph-pdp__slide')];
  const thumbs = [...root.querySelectorAll('[data-ph-thumb]')];
  const dots = [...root.querySelectorAll('.ph-pdp__dots span')];

  const selected = () =>
    fieldsets.map((fs) => {
      const checked = /** @type {HTMLInputElement|null} */ (fs.querySelector('input[type="radio"]:checked'));
      return checked ? checked.value : '';
    });

  const findVariant = (opts) => variants.find((v) => v.options.every((o, i) => o === opts[i]));

  const priceHtml = (v) => (v.compare ? `<s>${v.compare}</s> ${v.price}` : v.price);

  /* ---------- Gallery ---------- */
  const isMobile = () => window.matchMedia('(max-width: 989px)').matches;

  function goToSlide(index, smooth = true) {
    const slide = slides[index];
    if (!track || !slide) return;
    const opts = isMobile() ? { left: slide.offsetLeft } : { top: slide.offsetTop };
    track.scrollTo({ ...opts, behavior: smooth ? 'smooth' : 'auto' });
  }

  function setActive(index) {
    thumbs.forEach((t, i) => t.classList.toggle('is-active', i === index));
    dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
    const active = thumbs[index];
    active?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  if (track && slides.length > 1) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio > 0.55) setActive(Number(e.target.dataset.index));
        });
      },
      { root: track, threshold: [0.55] }
    );
    slides.forEach((s) => io.observe(s));
  }
  thumbs.forEach((t) => t.addEventListener('click', () => goToSlide(Number(t.dataset.phThumb))));

  /* ---------- Variant state ---------- */
  function refreshAvailability(opts) {
    fieldsets.forEach((fs, idx) => {
      fs.querySelectorAll('input[type="radio"]').forEach((input) => {
        const trial = [...opts];
        trial[idx] = input.value;
        const v = findVariant(trial);
        const label = input.closest('.ph-pdp__pill, .ph-pdp__color');
        label?.classList.toggle('is-unavailable', !v || !v.available);
        label?.classList.toggle('is-active', input.checked);
      });
      const valueEl = fs.querySelector('[data-ph-option-value]');
      if (valueEl) valueEl.textContent = opts[idx];
    });
  }

  function applyVariant(fromUser = true) {
    const opts = selected();
    const v = findVariant(opts);
    refreshAvailability(opts);

    stickySelects.forEach((sel) => {
      const i = Number(sel.dataset.phStickyOption);
      if (opts[i] !== undefined) sel.value = opts[i];
    });
    root.querySelectorAll('[data-ph-sticky-value]').forEach((el) => {
      const i = Number(el.dataset.phStickyValue);
      if (opts[i] !== undefined) el.textContent = opts[i];
    });

    const addButtons = root.querySelectorAll('[data-ph-add], [data-ph-sticky-atc]');
    const buyButton = /** @type {HTMLButtonElement|null} */ (root.querySelector('[data-ph-atc]'));
    if (!v) {
      addButtons.forEach((b) => {
        b.disabled = true;
        b.classList.remove('is-ready');
        b.querySelector('[data-ph-atc-label]').textContent = 'Unavailable';
      });
      if (buyButton) buyButton.hidden = true;
      return;
    }

    if (idInput) idInput.value = String(v.id);
    root.querySelectorAll('[data-ph-price]').forEach((el) => (el.innerHTML = priceHtml(v)));
    addButtons.forEach((b) => {
      b.disabled = !v.available;
      b.classList.toggle('is-ready', v.available);
      const label = b.hasAttribute('data-ph-sticky-atc') ? 'Add to cart' : 'Add to Cart';
      b.querySelector('[data-ph-atc-label]').textContent = v.available ? label : 'Sold out';
      b.querySelector('[data-ph-atc-price]').textContent = v.price;
    });
    if (buyButton) {
      buyButton.hidden = !v.available;
      buyButton.disabled = !v.available;
    }

    if (fromUser) {
      const url = new URL(location.href);
      url.searchParams.set('variant', String(v.id));
      history.replaceState(history.state, '', url.toString());
      if (v.media) {
        const idx = slides.findIndex((s) => Number(s.dataset.mediaId) === v.media);
        if (idx >= 0) goToSlide(idx);
      }
    }
  }

  fieldsets.forEach((fs) => fs.addEventListener('change', () => applyVariant()));
  stickySelects.forEach((sel) =>
    sel.addEventListener('change', () => {
      const fs = fieldsets[Number(sel.dataset.phStickyOption)];
      const input = /** @type {HTMLInputElement|null} */ (
        [...(fs?.querySelectorAll('input[type="radio"]') || [])].find((i) => i.value === sel.value)
      );
      if (input) input.checked = true;
      applyVariant();
    })
  );
  applyVariant(false);

  /* ---------- Add to cart (drawer opens via the standard cart event) ---------- */
  async function add(button) {
    if (!idInput?.value || button.disabled) return;
    button.classList.add('is-loading');
    const { ok } = await phAddToCart(button, idInput.value, 1);
    button.classList.remove('is-loading');
    if (ok) {
      button.classList.add('is-added');
      setTimeout(() => button.classList.remove('is-added'), 1500);
    }
  }

  /* ---------- Buy it now: straight to checkout with just this variant (cart permalink) ---------- */
  function buyNow(button) {
    if (!idInput?.value || button.disabled) return;
    button.classList.add('is-loading');
    window.location.href = `${Theme.routes.cart_url}/${idInput.value}:1`;
  }

  form?.addEventListener('submit', (e) => {
    e.preventDefault();
    const btn = /** @type {HTMLButtonElement|null} */ (root.querySelector('[data-ph-atc]'));
    if (btn) buyNow(btn);
  });
  root.querySelector('[data-ph-add]')?.addEventListener('click', (e) => add(/** @type {HTMLButtonElement} */ (e.currentTarget)));
  root.querySelector('[data-ph-sticky-atc]')?.addEventListener('click', (e) => add(/** @type {HTMLButtonElement} */ (e.currentTarget)));

  /* ---------- Mobile sticky bar: shown while the main button is off screen ---------- */
  const sticky = root.querySelector('[data-ph-sticky]');
  // Watch the whole options + buttons block so the bar never covers the size pills
  const actions = root.querySelector('[data-ph-form]') || root.querySelector('[data-ph-actions]');
  if (sticky && actions) {
    const setSticky = (visible) => {
      sticky.classList.toggle('is-visible', visible);
      sticky.setAttribute('aria-hidden', String(!visible));
      sticky.querySelectorAll('select, button').forEach((el) => (el.tabIndex = visible ? 0 : -1));
    };
    new IntersectionObserver(([entry]) => setSticky(!entry.isIntersecting && isMobile()), { threshold: 0 }).observe(actions);
  }
}

const boot = () => document.querySelectorAll('[data-ph-pdp]').forEach(initPdp);
boot();
document.addEventListener('shopify:section:load', boot);
